import type { FontVMetrics } from '@tsagko/label-zpl-pipeline'

/**
 * Canvas stand-in for Zebra's `^A0` font (CG Triumvirate Bold Condensed).
 *
 * Roboto Condensed Bold (SIL OFL 1.1, bundled locally — see
 * ../assets/fonts/) is a near-metric match for CG Triumvirate Bold
 * Condensed at NATURAL width: per-em advances measured 2026-06-11 via a
 * live Neodynamic render probe (^A0N,h,w; advance = advanceEm × w dots,
 * linear in w and independent of h):
 *
 *   glyph   ^A0 advance/w   RC-700 advance/em   delta
 *   'H'     0.6112          0.6138              +0.4%
 *   'M'     0.7588          0.7583              -0.07%
 *   '0'     0.4813          0.5054              +5.0%
 *
 * The pipeline emits `^A0,h,w` with w = h/2, so the canvas applies a
 * `scaleX(fontWidthMm / fontHeightMm)` condensation on top of this family
 * (see RenderPlanItem). Weight 700 chosen over 500: H/M advances measure
 * closer to ^A0 (0.6138/0.7583 vs 0.6167/0.7568 against 0.6112/0.7588)
 * and ^A0 is a BOLD face, so stroke weight matches too.
 */
export const CANVAS_TEXT_FONT_FAMILY =
  '"Roboto Condensed", "Arial Narrow", Arial, sans-serif'

/** Weight of the bundled face — must match the @font-face in index.css. */
export const CANVAS_TEXT_FONT_WEIGHT = 700

/**
 * Fallback for environments without real canvas text metrics (jsdom/SSR) or
 * when `actualBoundingBox*` is unavailable. Derived from the bundled Roboto
 * Condensed 700 (static v31) font tables under the CSS `line-height: 1`
 * half-leading model: unitsPerEm 2048, hhea ascender 1900 / descender -500,
 * OS/2 sCapHeight 1456 →
 *   baselineFrac = (1 − (1900+500)/2048)/2 + 1900/2048 ≈ 0.8418
 *   capTopFrac   = baselineFrac − 1456/2048            ≈ 0.1309
 * (hhea metrics are what browsers report as fontBoundingBox* for this font;
 * USE_TYPO_METRICS is not set). Runtime measurement supersedes this whenever
 * the browser provides the metrics.
 */
export const ROBOTO_CONDENSED_FALLBACK_VMETRICS: FontVMetrics = {
  capTopFrac: 0.1309,
  baselineFrac: 0.8418,
}

/**
 * Fraction of the font cell height for the alignment reference line:
 * cap-top (top), cap-center (middle), baseline (bottom). This is the line
 * that `capBandAnchorY` pins to the region per `vAlign`, so translating an
 * anchor by the difference in this fraction between two fonts keeps the
 * per-placement reference (not just the cap-top) coincident across fonts.
 */
export function capRefFrac(
  vAlign: 'top' | 'middle' | 'bottom',
  vm: FontVMetrics,
): number {
  if (vAlign === 'top') return vm.capTopFrac
  if (vAlign === 'bottom') return vm.baselineFrac
  return (vm.capTopFrac + vm.baselineFrac) / 2
}

const CAP_SAMPLE = '0123456789ABEHMX'
let cached: FontVMetrics | null = null

/**
 * Measure the canvas font's cap band as fractions of the font cell height,
 * mapped to the CSS `line-height: 1` box. Memoized (scale-invariant). Falls
 * back to {@link ROBOTO_CONDENSED_FALLBACK_VMETRICS} when metrics are
 * unavailable. While the bundled webfont is still loading (FontFaceSet
 * reports it unavailable) the fallback is returned WITHOUT caching, so a
 * later render re-measures the real font instead of pinning a fallback
 * measurement of "Arial Narrow"/Arial.
 */
export function measureCanvasFontVMetrics(
  fontFamily = CANVAS_TEXT_FONT_FAMILY,
  samplePx = 100,
): FontVMetrics {
  if (cached) return cached
  try {
    const fontShorthand = `${CANVAS_TEXT_FONT_WEIGHT} ${samplePx}px ${fontFamily}`
    const fonts = (document as Document & { fonts?: FontFaceSet }).fonts
    if (fonts && !fonts.check(fontShorthand)) {
      return ROBOTO_CONDENSED_FALLBACK_VMETRICS
    }
    const ctx = document.createElement('canvas').getContext('2d')
    if (!ctx) return (cached = ROBOTO_CONDENSED_FALLBACK_VMETRICS)
    ctx.font = fontShorthand
    ctx.textBaseline = 'alphabetic'
    const m = ctx.measureText(CAP_SAMPLE)
    const fbAsc = m.fontBoundingBoxAscent
    const fbDesc = m.fontBoundingBoxDescent
    const capAsc = m.actualBoundingBoxAscent
    if (![fbAsc, fbDesc, capAsc].every((v) => typeof v === 'number' && v > 0)) {
      return (cached = ROBOTO_CONDENSED_FALLBACK_VMETRICS)
    }
    const baselineFromTop = (samplePx - (fbAsc + fbDesc)) / 2 + fbAsc
    const capTopFromTop = baselineFromTop - capAsc
    cached = { capTopFrac: capTopFromTop / samplePx, baselineFrac: baselineFromTop / samplePx }
    return cached
  } catch {
    return (cached = ROBOTO_CONDENSED_FALLBACK_VMETRICS)
  }
}

/** Test-only: clear the memoized metrics. */
export function __resetCanvasFontVMetricsCache(): void {
  cached = null
}
